import type { GetStaticPropsContext } from 'next';

import { Heading, Stack, Text } from '@chakra-ui/react';
import { ArrowRightIcon } from '@udacity/chakra-uds-icons';

import { ButtonLink } from '~/components/button-link';
import { SectionCard } from '~/components/card';
import { ContentContainer } from '~/components/container';
import { Glyph } from '~/components/glyph';
import { PageCategory } from '~/features/analytics/constants';
import { trackEvent } from '~/features/analytics/track-event';
import { DEFAULT_LANGUAGE } from '~/features/internationalization/languages';
import { PageHead } from '~/features/page/components/page-head';
import { PageLayout } from '~/features/page/components/page-layout';
import { getPage } from '~/features/page/controllers/get-page';
import { Page } from '~/features/page/models/page';

const pageData = {
  pageTitle: 'Thank you for your interest in Udacity for Government.',
  blurb:
    'A Udacity team member will be in contact with you shortly to start a consultation and answer any questions you may have.',
  glyphName: 'checkmark-medal-two',
} as const;

export default function GovernmentContactSuccessPage({ page }: { page: Page }) {
  return (
    <>
      <PageHead page={page} pageCategory={PageCategory.GetStarted} />
      <PageLayout>
        <ContentContainer>
          <SectionCard>
            <Stack spacing={'40px'}>
              <Stack spacing={'16px'}>
                {pageData.glyphName && <Glyph height={64} name={pageData.glyphName} width={64} />}
                <Stack spacing={'8px'}>
                  <Heading as='h1' size='h1'>
                    {pageData.pageTitle}
                  </Heading>
                  <Text>{pageData.blurb}</Text>
                </Stack>
              </Stack>
              <ButtonLink
                buttonProps={{
                  rightIcon: <ArrowRightIcon h='24px' w='24px' />,
                  size: 'icon-right',
                  width: { base: '100%', md: 'fit-content' },
                  onClick: () =>
                    trackEvent({
                      type: 'click',
                      name: 'Get Started CTA Clicked',
                      label: 'browse_programs_clicked',
                    }),
                }}
                linkProps={{
                  href: '/catalog',
                }}
              >
                Browse Programs
              </ButtonLink>
            </Stack>
          </SectionCard>
        </ContentContainer>
      </PageLayout>
    </>
  );
}

export async function getStaticProps({ locale }: GetStaticPropsContext) {
  // Stop additional locale pages except the default locale from being built
  if (locale !== DEFAULT_LANGUAGE) {
    return {
      notFound: true,
    };
  }

  const [page] = await Promise.all([getPage('/get-started/government-success')]);

  return {
    props: {
      page,
    },
  };
}
